import { escapeHtml } from '@/lib/printLabel'
import {
  groupMovesByContainer,
  itemCode,
  itemDetail,
  itemKindLabel,
  type MoveGroup,
} from '@/components/equipment/storage/replace/movePreview'
import type { MoveItem, StorageTransferMove } from '@/types/storage'

/**
 * Pure logic behind printing a storage transfer (user request, Task 11): the
 * plan sheet's title and move grouping, and the single-move label's text and
 * markup.
 *
 * Kept out of the print components for the same reason `movePreview.ts` is:
 * `vitest.config.ts` only collects `src/**\/*.test.ts` in the `node`
 * environment, so a `.tsx` component would never be covered, and anything
 * worth asserting on belongs here.
 */

/** "Transfer plan ULT-01 → ULT-02", the plan sheet's title. */
export function transferPrintTitle(sourceCode: string, targetCode: string): string {
  return `Transfer plan ${routeLabel(sourceCode, targetCode)}`
}

/** "ULT-01 → ULT-02" — the sheet title's route, reused on the single-move label. */
export function routeLabel(sourceCode: string, targetCode: string): string {
  return `${sourceCode} → ${targetCode}`
}

/** `/inventory/storage-transfers/:transferId/print`, opened in a new tab from the plan page. */
export function transferPrintHref(transferId: string): string {
  return `/inventory/storage-transfers/${transferId}/print`
}

/**
 * One move's row status, in the same words `MovePreviewTable` renders on
 * screen (its inline ternary), so the printed sheet can never disagree with
 * the page it was printed from.
 */
export function moveStatusText(
  move: Pick<StorageTransferMove, 'status' | 'scannedAt' | 'skipReason' | 'targetNodeRef' | 'conditionVerdict'>,
): string {
  if (move.status === 'MOVED') return 'Moved'
  const excluded = move.status === 'SKIPPED'
  // A move left behind at the freezer is SKIPPED too, but it was planned and
  // then carried out as a skip; only a draft exclusion has no scan time.
  if (excluded && move.scannedAt) return `Skipped${move.skipReason ? `: ${move.skipReason}` : ''}`
  if (excluded) return 'Excluded'
  if (!move.targetNodeRef) return 'No destination'
  if (move.conditionVerdict !== 'ok') return move.conditionVerdict
  return 'Planned'
}

/**
 * Every move grouped by the box it comes out of, in the exact order
 * `MovePreviewTable` groups them in on screen — this wraps the same
 * `groupMovesByContainer` the table uses, rather than a second grouping that
 * could drift from the page a plan was printed off of.
 *
 * `groupMovesByContainer` is generic so this keeps `id` on each move, which
 * `ReviewableMove` (the table's own, narrower shape) does not carry and which
 * the sheet needs to build each row's QR url.
 */
export function sheetMoveGroups(moves: StorageTransferMove[]): MoveGroup<StorageTransferMove>[] {
  return groupMovesByContainer(moves)
}

/**
 * Above this many moves, the sheet draws no per-move QR at all.
 *
 * `TransferMoveQrDialog`'s own docblock warns a plan can be ten thousand
 * moves and deliberately avoids drawing a code per row on screen for that
 * reason ("generating ten thousand codes to show three of them would stall
 * the page"). Mounting that many `useQrSvg` calls at once on the print sheet
 * would stall it the same way (fix round 1).
 */
export const PER_MOVE_QR_LIMIT = 200

/** Whether the print sheet draws a QR per move, or just the plan's. */
export function showPerMoveQr(moveCount: number): boolean {
  return moveCount <= PER_MOVE_QR_LIMIT
}

/** Shown under the plan QR in place of the per-move codes, past the cap. */
export function tooManyMovesForQrNote(moveCount: number): string {
  return `This plan has ${moveCount} moves, too many to print a code for each. Scan the plan code above on a handset to work through them.`
}

/** What the single-move label says, before it is turned into markup. */
export interface MoveLabelContent {
  /** Large, mono — the identifier printed on the thing itself. */
  itemCode: string
  /** "Lot · material · Qty n · Exp date", or null when there is nothing to add. */
  itemLine: string | null
  fromPath: string
  toPath: string
  /** "ULT-01 → ULT-02". */
  routeLabel: string
  url: string
}

/** The single-move label's content: the move's own facts plus the plan it belongs to. */
export function moveLabelContent(
  move: Pick<StorageTransferMove, 'sourceNodePath' | 'targetNodePath' | 'item'>,
  sourceCode: string,
  targetCode: string,
  url: string,
): MoveLabelContent {
  return {
    itemCode: itemCode(move.item),
    itemLine: moveItemLine(move.item),
    fromPath: move.sourceNodePath,
    toPath: move.targetNodePath ?? 'no destination yet',
    routeLabel: routeLabel(sourceCode, targetCode),
    url,
  }
}

/** "Lot · Cryoprotectant A · Qty 5 · Exp 2026-01-01", the same composition the move-QR dialog's subtitle already uses. */
function moveItemLine(item: MoveItem | null): string | null {
  const kind = itemKindLabel(item)
  if (!kind) return null
  const detail = itemDetail(item)
  return detail ? `${kind} · ${detail}` : kind
}

/** Print rules for the single-move label: black on white, sized for one sheet. */
export const MOVE_LABEL_STYLE =
  '@page{margin:10mm}' +
  'body{font-family:ui-sans-serif,system-ui,-apple-system,sans-serif;margin:0;padding:16px;' +
  'color:#000;background:#fff}' +
  '.lbl{max-width:380px}' +
  '.lbl .code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:28px;' +
  'font-weight:700;word-break:break-all}' +
  '.lbl .line{margin-top:6px;font-size:13px}' +
  '.lbl .qr{margin:16px 0}' +
  '.lbl .qr svg{width:160px;height:160px}' +
  '.lbl .path{margin-top:10px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:14px}' +
  '.lbl .plan{margin-top:4px;font-size:12px;color:#333}' +
  '.lbl .url{margin-top:10px;font-size:10px;color:#555;word-break:break-all}'

/**
 * The single-move label as an HTML fragment for `printLabelViaIframe`.
 *
 * `svg` is the QR code already drawn for the move-QR dialog (`useQrSvg` in
 * `TransferPlanQr.tsx`) — printing never redraws it, so the label on paper is
 * exactly the code the person checked on screen before printing.
 */
export function buildMoveLabelHtml(content: MoveLabelContent, svg: string): string {
  const e = escapeHtml
  return (
    '<div class="lbl">' +
    `<div class="code">${e(content.itemCode)}</div>` +
    (content.itemLine ? `<div class="line">${e(content.itemLine)}</div>` : '') +
    (svg ? `<div class="qr">${svg}</div>` : '') +
    `<div class="path">${e(content.fromPath)} → ${e(content.toPath)}</div>` +
    `<div class="plan">Plan: ${e(content.routeLabel)}</div>` +
    `<div class="url">${e(content.url)}</div>` +
    '</div>'
  )
}
